/**
 * positionTableHelpers.tsx — shared layout constants and mini-components used
 * by PositionTableView, StrategyPositionsModal, and StrategyPositionsModalV2.
 *
 * Extracted here (no auth / apiClient / axios deps) so the two modal
 * components can import these utilities without pulling the auth chain through
 * Jest's module system. PositionTableView re-exports them for backwards compat.
 */
import React from 'react';
import { cn } from '../../utils/cn';
import { CHIP_SIDE } from '../../utils/chipColors';
import { shortRiskType as shortType } from '../../utils/triggerDescribe';
import type { PositionRow } from '../../types';

// ── Table header classes ──────────────────────────────────────────────────────

export const TH =
  'px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap';
export const TH_R =
  'px-3 py-2 text-right text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap';
export const TH_C =
  'px-3 py-2 text-center text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap';

// ── Side badge ────────────────────────────────────────────────────────────────

export const SideBadge: React.FC<{ side: string }> = ({ side }) => (
  <span
    className={cn(
      'inline-flex items-center justify-center text-[10px] font-bold w-10 h-5 rounded tabular-nums',
      side === 'BUY' ? CHIP_SIDE.BUY : CHIP_SIDE.SELL,
    )}
  >
    {side}
  </span>
);

// ── SL config display ─────────────────────────────────────────────────────────

// Percentage-family SL types (premium + UL) render with '%' suffix so a
// UL_PERCENTAGE SL doesn't drop the unit anchor. Widened per Phase 6
// review-fix H3. Points-family types have no suffix.
const PERCENT_SL_TYPES: readonly string[] = ['PERCENTAGE', 'UL_PERCENTAGE'];

export const slConfigDisplay = (p: PositionRow): string => {
  if (!p.slConfig) return '—';
  const suffix = PERCENT_SL_TYPES.includes(p.slConfig.type) ? '%' : '';
  return `${shortType(p.slConfig.type)} ${p.slConfig.value}${suffix}`;
};

// Target config hint, e.g. "UL PTS 20 pts" / "PCT 30%"; undefined when no target.
export const tpConfigDisplay = (p: PositionRow): string | undefined => {
  const cfg = p.targetProfitConfig;
  if (!cfg?.enabled) return undefined;
  return `${shortType(cfg.type)} ${cfg.value}${PERCENT_SL_TYPES.includes(cfg.type) ? '%' : ' pts'}`;
};

// ── Row background / opacity helpers ─────────────────────────────────────────

export const rowBg = (p: PositionRow, isSelected: boolean): string => {
  if (isSelected) return 'bg-blue-50 dark:bg-blue-900/20';
  if (p.entryStatus === 'REJECTED') return 'bg-red-50/30 dark:bg-red-900/10';
  if (p.status === 'EXIT_PENDING') return 'bg-amber-50/20 dark:bg-amber-900/10';
  if (p.status === 'CLOSED') return 'bg-gray-100 dark:bg-gray-800/60';
  return '';
};

// Closed rows: grayscale + mild fade so live rows stand out.
export const rowOpacity = (p: PositionRow): string =>
  p.status === 'CLOSED' ? 'opacity-75 grayscale' : '';
